import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/auth/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { fmtFull, parseIso, fmtShort, MOIS } from "@/lib/date";
import {
  AlertTriangle, ArrowRight, CalendarClock, CalendarCheck,
  Clock, Gauge, ListChecks, Users, Sparkles,
} from "lucide-react";
import { Progress } from "@/components/ui/progress";

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/dashboard")
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-[#6b7f79]">Chargement du tableau de bord…</div>;
  if (!data) return <div className="text-[#6b7f79]">Aucune donnée</div>;

  const today = new Date();
  const greeting = greetingFor(today);

  if (user?.role === "student") return <StudentDashboard data={data} greeting={greeting} user={user} />;
  return <AdminDashboard data={data} greeting={greeting} user={user} />;
}

function greetingFor(d) {
  const h = d.getHours();
  if (h < 11) return "Bonjour";
  if (h < 18) return "Bon après-midi";
  return "Bonsoir";
}

function StudentDashboard({ data, greeting, user }) {
  const quota = data.quota_year_hours || 650;
  const pct = data.quota_used_pct || 0;
  const today = new Date();
  const [me, setMe] = useState(null);
  useEffect(() => {
    api.get("/auth/me").then((r) => setMe(r.data)).catch(() => {});
  }, []);
  const PROFILE_KEYS = [
    "first_name", "last_name", "phone", "date_of_birth", "national_register",
    "address_street", "address_postal_code", "address_city", "address_country",
    "iban", "emergency_contact_name", "emergency_contact_phone", "emergency_contact_relation",
  ];
  const missingCount = me ? PROFILE_KEYS.filter((k) => !me[k]).length : 0;
  return (
    <div className="space-y-6" data-testid="student-dashboard">
      <div>
        <p className="label-tiny">{fmtFull(today)}</p>
        <h1 className="font-display text-3xl sm:text-4xl text-[#1A2B27] mt-1">
          {greeting}, {user.name} 👋
        </h1>
        <p className="text-[#6b7f79] mt-1">Voici tes prochains shifts et l&apos;état du mois.</p>
      </div>

      {me && missingCount > 0 && (
        <Card className="card-soft border-0 bg-[#FFF4E5] border border-[#FFB300]" data-testid="dashboard-profile-incomplete">
          <CardContent className="p-4 flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-[#8B5A00] shrink-0" />
            <div className="flex-1 text-sm">
              <span className="font-semibold text-[#1A2B27]">
                Ton profil est incomplet ({missingCount} champ{missingCount > 1 ? "s" : ""} à compléter)
              </span>
              <span className="text-[#6b7f79] ml-1">— utile pour ton dossier atelier.</span>
            </div>
            <Link to="/profil">
              <Button size="sm" data-testid="dashboard-profile-cta" className="rounded-full bg-[#2D5951] hover:bg-[#1F423C]">
                Compléter
              </Button>
            </Link>
          </CardContent>
        </Card>
      )}

      {false && data.missing_entries > 0 && (
        <Card className="card-soft border-0 bg-[#E8F0EE]" data-testid="alert-missing-entries">
          <CardContent className="p-4 flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-[#2D5951] shrink-0" />
            <div className="flex-1 text-sm">
              <span className="font-semibold text-[#1A2B27]">
                {data.missing_entries} journée{data.missing_entries > 1 ? "s" : ""} à encoder
              </span>
              <span className="text-[#6b7f79] ml-1">— pense à indiquer tes heures.</span>
            </div>
            <Link to="/heures">
              <Button size="sm" className="rounded-full bg-[#2D5951] hover:bg-[#1F423C]">Encoder</Button>
            </Link>
          </CardContent>
        </Card>
      )}

      {/* Mini-tableau « Ma progression » — motivationnel */}
      <ProgressWidget data={data} quota={quota} pct={pct} />

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard
          icon={<Clock className="w-5 h-5" />}
          label="Mois en cours"
          value={data.month_hhmm}
          sub={`${data.month_hours} h prestées`}
          testId="stat-month"
        />
        <StatCard
          icon={<Gauge className="w-5 h-5" />}
          label="Année en cours"
          value={`${data.year_hours} h`}
          sub={`sur ${quota} h (quota)`}
          testId="stat-year"
        >
          <Progress value={pct} className="h-2 mt-3 bg-[#F0F5F3]" />
          <p className="text-[10px] text-[#94ADA7] mt-1">{pct}% utilisés</p>
        </StatCard>
        <StatCard
          icon={<CalendarCheck className="w-5 h-5" />}
          label="Trimestres"
          value=""
          sub=""
          testId="stat-quarters"
        >
          <div className="grid grid-cols-4 gap-1.5 mt-1">
            {data.quarter_hours.map((q, i) => (
              <div key={`T${i + 1}`} className="text-center">
                <div className="text-xs font-bold text-[#1A2B27]">{q}h</div>
                <div className="text-[10px] text-[#94ADA7]">T{i + 1}</div>
              </div>
            ))}
          </div>
        </StatCard>
      </div>

      <section>
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display text-xl text-[#1A2B27] flex items-center gap-2">
            <CalendarClock className="w-5 h-5 text-[#2D5951]" /> Mes prochains shifts
          </h2>
          <Link to="/planning" className="text-sm text-[#2D5951] font-semibold flex items-center gap-1 hover:underline">
            Voir tout <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        {data.upcoming_shifts.length === 0 ? (
          <Card className="card-soft border-0">
            <CardContent className="p-6 text-center text-[#94ADA7]">
              Aucun shift publié à venir. Encode tes disponibilités pour la prochaine période.
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2">
            {data.upcoming_shifts.map((s) => (
              <ShiftRow key={s.id} shift={s} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function AdminDashboard({ data, greeting, user }) {
  const today = new Date();
  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      <div>
        <p className="label-tiny">{fmtFull(today)}</p>
        <h1 className="font-display text-3xl sm:text-4xl text-[#1A2B27] mt-1">
          {greeting}, {user.name}
        </h1>
        <p className="text-[#6b7f79] mt-1">Pilote ton atelier en un coup d'œil.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatCard
          icon={<Users className="w-5 h-5" />}
          label="Aujourd'hui"
          value={`${data.todays_shifts.length} planifié${data.todays_shifts.length > 1 ? "s" : ""}`}
          sub={data.todays_shifts.map((s) => s.student_name).join(" · ") || "Aucun shift"}
          testId="stat-today"
        />
        <StatCard
          icon={<ListChecks className="w-5 h-5" />}
          label="Dispos manquantes"
          value={String(data.missing_availabilities.length)}
          sub={data.open_period ? `Période ${data.open_period}` : "Pas de période ouverte"}
          testId="stat-missing-availabilities"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="card-soft border-0" data-testid="card-todays-shifts">
          <CardContent className="p-5">
            <h2 className="font-display text-lg text-[#1A2B27] mb-3 flex items-center gap-2">
              <CalendarClock className="w-5 h-5 text-[#2D5951]" /> Aujourd'hui à l'atelier
            </h2>
            {data.todays_shifts.length === 0 ? (
              <p className="text-sm text-[#94ADA7]">Aucun shift planifié pour aujourd'hui.</p>
            ) : (
              <div className="space-y-2">
                {data.todays_shifts.map((s) => (
                  <div key={s.id} className="flex items-center justify-between p-3 rounded-xl bg-[#FFFFFF] border border-[#DDE7E4]">
                    <div>
                      <div className="font-semibold text-[#1A2B27]">{s.student_name}</div>
                      <div className="text-xs text-[#94ADA7]">{s.start} → {s.end}</div>
                    </div>
                    <Badge variant="outline" className="border-[#2D5951] text-[#2D5951] rounded-full">
                      <Sparkles className="w-3 h-3 mr-1" /> Au four
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="card-soft border-0" data-testid="card-month-totals">
          <CardContent className="p-5">
            <h2 className="font-display text-lg text-[#1A2B27] mb-3 flex items-center gap-2">
              <Clock className="w-5 h-5 text-[#2D5951]" /> Mois en cours
            </h2>
            <div className="space-y-2 max-h-72 overflow-auto pr-1">
              {data.monthly_totals.map((m) => (
                <div key={m.student_id} className="flex items-center justify-between text-sm">
                  <span className="text-[#1A2B27] font-medium">{m.student_name}</span>
                  <span className="font-mono text-[#6b7f79]">{m.hhmm}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {data.quota_alerts && data.quota_alerts.length > 0 && (
        <Card className="card-soft border-0 bg-[#E8F0EE] border border-[#5C7F76]" data-testid="dashboard-quota-alerts">
          <CardContent className="p-5">
            <h3 className="font-semibold text-[#1A2B27] flex items-center gap-2 mb-3">
              <AlertTriangle className="w-4 h-4 text-[#2D5951]" /> Alertes quota annuel
            </h3>
            <div className="space-y-2">
              {data.quota_alerts.map((q) => (
                <div key={q.student_id} className="flex items-center justify-between p-2.5 rounded-xl bg-white border border-[#C9DBD6]" data-testid={`dashboard-quota-item-${q.student_id}`}>
                  <div>
                    <div className="font-semibold text-[#1A2B27]">{q.student_name}</div>
                    <div className="text-xs text-[#94ADA7]">
                      {q.projected_hours} h prévues / {q.quota_year_hours} h
                      {q.over && <span className="ml-1 font-bold text-[#E53935]">— dépassé de {q.over_hours} h</span>}
                    </div>
                  </div>
                  <Badge className={`rounded-full ${q.over ? "bg-[#E53935] hover:bg-[#E53935]" : "bg-[#FFB300] hover:bg-[#FFB300]"} text-white`}>
                    {q.percent}%
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {data.missing_availabilities.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card className="card-soft border-0 bg-[#F0F5F3]">
            <CardContent className="p-5">
              <h3 className="font-semibold text-[#1A2B27] flex items-center gap-2 mb-2">
                <ListChecks className="w-4 h-4 text-[#3E6E64]" /> Dispos non transmises
              </h3>
              <ul className="text-sm text-[#6b7f79]">
                {data.missing_availabilities.map((m) => (
                  <li key={m.student_id}>{m.student_name}</li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}

function ProgressWidget({ data, quota, pct }) {
  const monthHours = Number(data.month_hours || 0);
  const yearHours = Number(data.year_hours || 0);
  const remaining = Math.max(0, quota - yearHours);
  const q = data.quota_year_hours || 650;
  const monthGoal = Math.round(q / 12); // objectif mois indicatif
  const monthPct = monthGoal > 0 ? Math.min(100, Math.round((monthHours / monthGoal) * 100)) : 0;

  // Message motivationnel contextuel
  let mood = "🌱 En route !";
  let moodText = "Belle régularité — continue comme ça !";
  if (pct >= 100) { mood = "🏆 Quota atteint"; moodText = `Bravo, tu as complété tes ${quota} h annuelles !`; }
  else if (pct >= 80) { mood = "🔥 Presque au bout"; moodText = `Plus que ${remaining} h avant d'atteindre le quota.`; }
  else if (pct >= 50) { mood = "💪 Sur la bonne voie"; moodText = `Encore ${remaining} h à faire — tu es à mi-parcours.`; }
  else if (pct >= 20) { mood = "✨ Bien parti"; moodText = `Beau démarrage. Plus que ${remaining} h pour le quota.`; }
  else if (yearHours > 0) { mood = "🌱 Ça commence"; moodText = `Encore ${remaining} h à faire cette année.`; }
  else { mood = "🚀 Prêt à démarrer"; moodText = `Tu peux prester jusqu'à ${quota} h cette année.`; }

  return (
    <Card className="card-soft border-0 bg-gradient-to-br from-[#2D5951] to-[#1F423C] text-white overflow-hidden relative" data-testid="progress-widget">
      <div className="absolute -right-8 -top-8 w-40 h-40 rounded-full bg-white/5 pointer-events-none" />
      <div className="absolute -right-16 -bottom-16 w-48 h-48 rounded-full bg-white/5 pointer-events-none" />
      <CardContent className="p-5 sm:p-6 relative">
        <div className="flex items-center gap-2 mb-4">
          <Sparkles className="w-4 h-4 text-[#B8DBD4]" />
          <span className="text-[10px] font-semibold uppercase tracking-[0.15em] text-[#B8DBD4]">Ma progression</span>
        </div>

        {/* Mini-tableau 2 colonnes */}
        <div className="grid grid-cols-2 gap-4 mb-5">
          <div data-testid="progress-month">
            <div className="text-[10px] uppercase tracking-wider text-[#B8DBD4]/80">Heures ce mois</div>
            <div className="flex items-baseline gap-1.5 mt-1">
              <span className="font-display text-4xl leading-none">{monthHours}</span>
              <span className="text-sm opacity-70">h</span>
            </div>
            {monthGoal > 0 && (
              <div className="mt-2">
                <div className="h-1.5 rounded-full bg-white/15 overflow-hidden">
                  <div
                    className="h-full bg-[#B8DBD4] rounded-full transition-all duration-500"
                    style={{ width: `${monthPct}%` }}
                    data-testid="progress-month-bar"
                  />
                </div>
                <p className="text-[10px] text-[#B8DBD4]/80 mt-1">
                  {monthPct}% de l&apos;objectif mensuel ({monthGoal} h)
                </p>
              </div>
            )}
          </div>

          <div data-testid="progress-year">
            <div className="text-[10px] uppercase tracking-wider text-[#B8DBD4]/80">Quota annuel</div>
            <div className="flex items-baseline gap-1.5 mt-1">
              <span className="font-display text-4xl leading-none" data-testid="progress-year-pct">{pct}%</span>
              <span className="text-sm opacity-70">/ 100</span>
            </div>
            <div className="mt-2">
              <div className="h-1.5 rounded-full bg-white/15 overflow-hidden">
                <div
                  className="h-full bg-[#FFD54F] rounded-full transition-all duration-500"
                  style={{ width: `${Math.min(100, pct)}%` }}
                  data-testid="progress-year-bar"
                />
              </div>
              <p className="text-[10px] text-[#B8DBD4]/80 mt-1">
                {yearHours} h / {quota} h — reste {remaining} h
              </p>
            </div>
          </div>
        </div>

        {/* Message contextuel */}
        <div className="flex items-center gap-2 p-2.5 rounded-xl bg-white/8 backdrop-blur-sm" data-testid="progress-mood">
          <span className="font-semibold text-sm">{mood}</span>
          <span className="text-xs text-[#B8DBD4]">— {moodText}</span>
        </div>
      </CardContent>
    </Card>
  );
}

function StatCard({ icon, label, value, sub, children, testId }) {
  return (
    <Card className="card-soft border-0 transition-all duration-200 hover:-translate-y-0.5" data-testid={testId}>
      <CardContent className="p-5">
        <div className="flex items-center gap-2 text-[#94ADA7]">
          {icon}
          <span className="label-tiny">{label}</span>
        </div>
        {value && <div className="font-display text-3xl text-[#1A2B27] mt-2 leading-none">{value}</div>}
        {sub && <div className="text-xs text-[#94ADA7] mt-1.5">{sub}</div>}
        {children}
      </CardContent>
    </Card>
  );
}

function ShiftRow({ shift }) {
  const d = parseIso(shift.date);
  return (
    <Card className="card-soft border-0 transition-all duration-200 hover:translate-x-1" data-testid={`shift-row-${shift.id}`}>
      <CardContent className="p-4 flex items-center gap-4">
        <div className="text-center w-14 shrink-0">
          <div className="text-[10px] uppercase tracking-wider text-[#94ADA7] font-semibold">
            {["Dim", "Lun", "Mar", "Mer", "Jeu", "Ven", "Sam"][d.getDay()]}
          </div>
          <div className="font-display text-2xl text-[#2D5951] leading-none">{d.getDate()}</div>
          <div className="text-[10px] text-[#94ADA7] uppercase">{MOIS[d.getMonth()].slice(0, 3)}</div>
        </div>
        <div className="flex-1">
          <div className="font-semibold text-[#1A2B27]">{shift.start} → {shift.end}</div>
          {shift.note && <div className="text-xs text-[#94ADA7] mt-0.5">{shift.note}</div>}
        </div>
      </CardContent>
    </Card>
  );
}
